"use client";

import { IBaseLabelValue } from "@/interfaces";
import { useRouter } from "next/navigation";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "../../ui";
import { cn } from "@/lib";
import InputDataContent from "../inputDataContent";
import { routes } from "@/routes";
import { useTranslations } from "next-intl";
import DownloadPdf from "../../pdfTemplates/downloadPdf";
import { useRef } from "react";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import { IRawPurchasedEducationalExemptionInquiryResData } from "@/interfaces/inquiries/governmental";

const EducationalExemptionInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedEducationalExemptionInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const {
    requestStatusTypeTitle,
    educationStartDate,
    exemptionEndDate,
    graduationEndDate,
    errorMessage,
  } = data.data.data;
  const { national_code } = data.data.input;
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("educationalExemption.inputLabel"),
      value: national_code,
    },
  ];

  const resultContent: IBaseLabelValue[] = [
    {
      label: t("educationalExemption.requestStatusTypeTitle"),
      value: requestStatusTypeTitle,
    },
    {
      label: t("educationalExemption.educationStartDate"),
      value: educationStartDate ? educationStartDate.split("-")[0] : "نامشخص",
    },
    {
      label: t("educationalExemption.exemptionEndDate"),
      value: exemptionEndDate ? exemptionEndDate.split("-")[0] : "نامشخص",
    },
    {
      label: t("educationalExemption.graduationEndDate"),
      value: graduationEndDate ? graduationEndDate.split("-")[0] : "نامشخص",
    },
  ];

  const pdfContentRef = useRef<HTMLDivElement>(null);
  return (
    <div className="shadow-main from-teal-ocean relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-8 text-center font-bold lg:text-lg">
          {t("educationalExemption.title")}
        </h2>

        <InputDataContent content={inputContent} />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("educationalExemption.resultTitle")}
          </h2>
          <DownloadPdf
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            id="educational-exemption-inquiry-download-pdf"
            fileName={`استعلام-معافیت-تحصیلی ${national_code}`}
          />
        </FlexBetween>
        <FlexCenter className={cn("block w-full md:flex")}>
          {!errorMessage ? (
            <InputDataContent
              className={cn("w-full")}
              content={resultContent}
            />
          ) : (
            <div className="shadow-main mb-10 flex w-full flex-col gap-8 space-y-2 rounded-xl p-6 text-center">
              {errorMessage}
            </div>
          )}
        </FlexCenter>
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="educational-exemption-inquiry-download-pdf"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
            fileName={`استعلام-معافیت-تحصیلی ${national_code}`}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-educational-exemption-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-educational-exemption-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Educational_Exemption.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
        {/* This div will be used for PDF generation - DetailCreditCard style */}
        <div
          ref={pdfContentRef}
          style={{
            position: "fixed",
            left: "-9999px",
            top: "-9999px",
            width: "800px",
            fontFamily: "IRANYekan, sans-serif",
            margin: "2rem auto",
          }}
        >
          <PdfTemplate
            tableTitle={`گزارش وضعیت صدور کارت پایان خدمت ${national_code}`}
            resultContent={resultContent}
            code={data.code}
            dateTime={data.created_at}
          />
        </div>
      </FlexCol>
    </div>
  );
};

export default EducationalExemptionInquiryDetails;
